import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useRef } from 'react';
import { Box, Carousel, CarouselVariant, Column, Heading, HeadingType, I18N_NAMESPACES, SliderNavColor, SliderNavVariant, Tag, TagColor, } from '@as/cotopaxi';
import { MIN_SCREEN_WIDTH_DESKTOP, MIN_SCREEN_WIDTH_DESKTOP_LARGE, MIN_SCREEN_WIDTH_TABLET, } from '../../constants/ScreenWidthConstants';
import { SLIDES_AMOUNT_DESKTOP, SLIDES_AMOUNT_DESKTOP_LARGE } from '../../constants/SliderConstants';
import { useTranslation } from 'react-i18next';
import { ProductGroupPdpComparisonProduct } from './ProductGroupPdpComparisonProduct';
import { useProductTileHelpers } from '../../hooks/useProductTileHelpers';
import { useSelector } from 'react-redux';
import { makeGetProduct } from '../../redux/product/selectors/ProductSelector';
import { mergeProductFeatures } from '@as-react/common-utils';
import { useProductGroupAnalytics } from '../../hooks/useProductGroupAnalytics';
import { createProductDetailToProductListerAdapter } from '../../util/ProductUtil';
const SLIDES_AMOUNT_MOBILE = 1.5;
const SLIDES_AMOUNT_TABLET = 3.5;
const MAX_PRODUCTS_SIZE = 5;
const MAX_FEATURES_SIZE = 5;
var GRID_SPACING;
(function (GRID_SPACING) {
    GRID_SPACING[GRID_SPACING["MOBILE"] = 1] = "MOBILE";
    GRID_SPACING[GRID_SPACING["TABLET"] = 2] = "TABLET";
})(GRID_SPACING || (GRID_SPACING = {}));
export const GRID_BOX_PARAMS = {
    mobile: {
        spacing: {
            marginRight: GRID_SPACING.MOBILE,
            marginLeft: GRID_SPACING.MOBILE,
        },
    },
    tablet: {
        spacing: {
            marginRight: GRID_SPACING.TABLET,
            marginLeft: GRID_SPACING.TABLET,
        },
    },
};
export var GRID_AREAS;
(function (GRID_AREAS) {
    GRID_AREAS["PRODUCT"] = "product";
    GRID_AREAS["PRODUCT_TILE"] = "tile";
    GRID_AREAS["FEATURE"] = "feature";
    GRID_AREAS["FEATURE_TITLE"] = "title";
    GRID_AREAS["FEATURE_VALUE"] = "value";
    GRID_AREAS["COMPARE"] = "compare";
})(GRID_AREAS || (GRID_AREAS = {}));
const repeat = (count, prefix, children = null) => {
    if (!children) {
        return Array.from({ length: count }, (_, index) => (count > 1 ? `${prefix}-${index}` : `${prefix}`));
    }
    else {
        return Array.from({ length: count }, (_, index) => children.map(child => `${prefix}-${index}-${child}`)).flat(1);
    }
};
const generateGridTemplateAreas = (columnStructure, rowStructure) => {
    let templateAreas = '';
    const rows = rowStructure.flat(2);
    const columns = columnStructure.flat(2);
    rows.forEach(row => {
        let templateAreaRow = '"';
        columns.forEach(col => {
            templateAreaRow += `${col}-${row} `;
        });
        templateAreas += templateAreaRow.trim() + '"\n';
    });
    return templateAreas.trim();
};
const COMPARE_PRODUCT_INDEX_ADDITION = 1;
const GRID_TEMPLATE_AREAS = generateGridTemplateAreas([repeat(MAX_PRODUCTS_SIZE, GRID_AREAS.PRODUCT)], [
    [GRID_AREAS.PRODUCT_TILE],
    repeat(MAX_FEATURES_SIZE, GRID_AREAS.FEATURE, [GRID_AREAS.FEATURE_TITLE, GRID_AREAS.FEATURE_VALUE]),
    [GRID_AREAS.COMPARE],
]);
export const ProductGroupPdpComparison = ({ analyticsAreaName, productGroupId, products, title, productsFeaturesRecord, analyticsProductGroupName, currentProductHasFeatures, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const { handleWishlistToggle, handleUpdateCompareList, compareItems } = useProductTileHelpers();
    const productUniqueKey = useId();
    const productDetail = useSelector(makeGetProduct());
    const productGroupRef = useRef(null);
    const { handleItemClickAnalytics } = useProductGroupAnalytics(Boolean(analyticsProductGroupName), productGroupRef, analyticsProductGroupName, title, products);
    if (!(products === null || products === void 0 ? void 0 : products.length)) {
        return null;
    }
    const currentProduct = currentProductHasFeatures && productDetail && productDetail.productColorVariations
        ? createProductDetailToProductListerAdapter(productDetail)
        : null;
    const productsWithFeatures = mergeProductFeatures(currentProduct ? [currentProduct, ...products] : products, productsFeaturesRecord, MAX_PRODUCTS_SIZE, currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.productId, MAX_FEATURES_SIZE);
    const createCurrentProductTag = () => (_jsx(Tag, { color: TagColor.SEPTENARY, children: t('product.group.current.tag') }, "current_product_tag"));
    const carouselConfig = {
        variant: CarouselVariant.PRODUCT_TILE_COMPARISON,
        arrowsConfig: {
            color: SliderNavColor.BASE,
            variant: SliderNavVariant.CIRCLE,
        },
        showPagination: false,
        fullWidth: true,
        configurationOverrides: {
            preventClicks: true,
            slidesPerView: SLIDES_AMOUNT_MOBILE,
            spaceBetween: 0,
            watchOverflow: true,
            shouldSwiperUpdate: true,
            breakpoints: {
                [MIN_SCREEN_WIDTH_TABLET]: {
                    slidesPerView: SLIDES_AMOUNT_TABLET,
                },
                [MIN_SCREEN_WIDTH_DESKTOP]: {
                    slidesPerView: SLIDES_AMOUNT_DESKTOP,
                },
                [MIN_SCREEN_WIDTH_DESKTOP_LARGE]: {
                    slidesPerView: SLIDES_AMOUNT_DESKTOP_LARGE,
                },
            },
        },
    };
    const mainBoxParams = {
        mobile: {
            spacing: {
                paddingBottom: 0,
                paddingTop: 2,
            },
        },
        tablet: {
            spacing: {
                paddingBottom: 6,
                paddingTop: 0,
                marginTop: 0,
            },
        },
        desktop: { spacing: { paddingBottom: 7 } },
        dataAttributes: { 'data-analytics-bannerid': analyticsAreaName },
    };
    const innerBoxParams = {
        mobile: {
            spacing: {
                paddingBottom: 3,
                paddingTop: 2,
            },
            bleed: {
                marginInline: GRID_SPACING.MOBILE,
            },
        },
        tablet: {
            spacing: {
                paddingBottom: 0,
                paddingTop: 0,
                marginTop: 3,
            },
            bleed: {
                marginInline: GRID_SPACING.TABLET,
            },
        },
        desktop: { spacing: { marginTop: 3 } },
    };
    const gridAreasStyle = {
        '--pdp-comparison-grid-template-areas': GRID_TEMPLATE_AREAS,
    };
    return (_jsxs(Box, Object.assign({ innerRef: productGroupRef }, mainBoxParams, { children: [title && _jsx(Heading, { type: HeadingType.SUBTITLE, children: title }), _jsx(Box, Object.assign({}, innerBoxParams, { children: !!productsWithFeatures && (_jsx("div", { style: gridAreasStyle, children: _jsx(Carousel, Object.assign({}, carouselConfig, { children: productsWithFeatures.map((compareProduct, productIndex) => (_jsx(Column, { desktop: SLIDES_AMOUNT_DESKTOP, children: _jsx(ProductGroupPdpComparisonProduct, { compareItems: compareItems, hideTags: Boolean(currentProduct && productIndex !== 0), customTags: compareProduct.productId === (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.productId) ? createCurrentProductTag() : undefined, onWishListToggle: handleWishlistToggle, parentId: productGroupId, product: compareProduct, productIndex: productIndex, productTileFormat: "simple", updateCompareList: handleUpdateCompareList, onTileClick: () => {
                                    handleItemClickAnalytics(productIndex + COMPARE_PRODUCT_INDEX_ADDITION, compareProduct);
                                } }) }, `${productUniqueKey}-${compareProduct.productCode}_${compareProduct.colourId}`))) })) })) }))] })));
};
